import { Capacitor } from "@capacitor/core";
import { Haptics, NotificationType } from "@capacitor/haptics";
import { Share } from "@capacitor/share";
import { Filesystem, Directory } from "@capacitor/filesystem";

export const WEB_ORIGIN = (process.env.REACT_APP_BACKEND_URL || "https://events.imu7.com").replace(/\/$/, "");

export const isNativeApp = () => Capacitor.isNativePlatform();

export async function captureElementPng(el) {
  const html2canvas = (await import("html2canvas")).default;
  const origCanvases = el.querySelectorAll("canvas");
  const canvas = await html2canvas(el, {
    backgroundColor: "#ffffff",
    scale: 2,
    useCORS: true,
    logging: false,
    onclone: (doc, clonedEl) => {
      doc.querySelectorAll(".print\\:hidden, .no-print").forEach((n) => (n.style.display = "none"));
      const cloneCanvases = (clonedEl || doc).querySelectorAll("canvas");
      cloneCanvases.forEach((c, i) => {
        try {
          const orig = origCanvases[i];
          if (!orig) return;
          const img = doc.createElement("img");
          img.src = orig.toDataURL("image/png");
          const r = orig.getBoundingClientRect();
          img.style.width = `${r.width}px`;
          img.style.height = `${r.height}px`;
          img.style.display = "block";
          c.replaceWith(img);
        } catch {}
      });
    },
  });
  const dataUrl = canvas.toDataURL("image/png");
  canvas.width = 0;
  canvas.height = 0;
  return dataUrl;
}

function roundRectPath(x, cx, cy, w, h, r) {
  x.beginPath();
  x.moveTo(cx + r, cy);
  x.arcTo(cx + w, cy, cx + w, cy + h, r);
  x.arcTo(cx + w, cy + h, cx, cy + h, r);
  x.arcTo(cx, cy + h, cx, cy, r);
  x.arcTo(cx, cy, cx + w, cy, r);
  x.closePath();
}

export function composeTicketPng({ code, name, eventTitle, eventDate, qrCanvas, brand, ticketLabel, codeLabel, nameLabel, dateLabel }) {
  const S = 2;
  const W = 600 * S, H = 900 * S;
  const cv = document.createElement("canvas");
  cv.width = W;
  cv.height = H;
  const x = cv.getContext("2d");
  const font = (px, weight) => `${weight} ${px * S}px -apple-system, "Segoe UI", "Noto Sans Arabic", sans-serif`;
  const bg = "#f1f5f9";

  x.fillStyle = bg;
  x.fillRect(0, 0, W, H);

  // card
  const cx0 = 36 * S, cy0 = 36 * S, cw = W - 72 * S, ch = H - 72 * S, cr = 28 * S;
  roundRectPath(x, cx0, cy0, cw, ch, cr);
  x.fillStyle = "#ffffff";
  x.fill();
  x.strokeStyle = "#e2e8f0";
  x.lineWidth = 2;
  roundRectPath(x, cx0, cy0, cw, ch, cr);
  x.stroke();

  // top accent bar (clipped to card)
  x.save();
  roundRectPath(x, cx0, cy0, cw, ch, cr);
  x.clip();
  const grad = x.createLinearGradient(cx0, 0, cx0 + cw, 0);
  grad.addColorStop(0, "#2563eb");
  grad.addColorStop(0.65, "#3b82f6");
  grad.addColorStop(1, "#f59e0b");
  x.fillStyle = grad;
  x.fillRect(cx0, cy0, cw, 9 * S);
  x.restore();

  x.textAlign = "center";
  const text = (str, xx, y, px, weight, color) => {
    x.fillStyle = color;
    x.font = font(px, weight);
    x.fillText(str, xx, y * S);
  };

  // brand badge
  const bGrad = x.createLinearGradient(W / 2 - 24 * S, 66 * S, W / 2 + 24 * S, 114 * S);
  bGrad.addColorStop(0, "#2563eb");
  bGrad.addColorStop(1, "#f59e0b");
  x.fillStyle = bGrad;
  roundRectPath(x, W / 2 - 24 * S, 66 * S, 48 * S, 48 * S, 13 * S);
  x.fill();
  text("V", W / 2, 100, 28, "bold", "#ffffff");

  text(brand || "منصة تسجيل الفعاليات", W / 2, 140, 18, "bold", "#2563eb");
  text("E - T I C K E T", W / 2, 162, 10, "600", "#94a3b8");

  if (eventTitle) text(eventTitle, W / 2, 206, 28, "bold", "#0f172a");
  if (ticketLabel) text(ticketLabel, W / 2, 234, 15, "600", "#64748b");

  // info columns
  const infoY = 272;
  if (name && eventDate) {
    text((nameLabel || "الاسم").toUpperCase(), W * 0.3, infoY, 11, "600", "#94a3b8");
    text(name, W * 0.3, infoY + 26, 17, "bold", "#0f172a");
    text((dateLabel || "التاريخ").toUpperCase(), W * 0.7, infoY, 11, "600", "#94a3b8");
    text(eventDate, W * 0.7, infoY + 26, 17, "bold", "#0f172a");
  } else if (name || eventDate) {
    const label = name ? nameLabel || "الاسم" : dateLabel || "التاريخ";
    text(label.toUpperCase(), W / 2, infoY, 11, "600", "#94a3b8");
    text(name || eventDate, W / 2, infoY + 26, 17, "bold", "#0f172a");
  }

  // QR box
  const qr = 290 * S;
  const qrTop = 330 * S;
  roundRectPath(x, W / 2 - qr / 2 - 18 * S, qrTop, qr + 36 * S, qr + 36 * S, 20 * S);
  x.fillStyle = "#ffffff";
  x.fill();
  x.strokeStyle = "#e2e8f0";
  x.lineWidth = 2.5 * S;
  roundRectPath(x, W / 2 - qr / 2 - 18 * S, qrTop, qr + 36 * S, qr + 36 * S, 20 * S);
  x.stroke();
  if (qrCanvas) {
    x.imageSmoothingEnabled = false;
    x.drawImage(qrCanvas, W / 2 - qr / 2, qrTop + 18 * S, qr, qr);
  }

  // perforated divider + notches
  const dy = 720 * S;
  x.save();
  x.strokeStyle = "#cbd5e1";
  x.lineWidth = 2.5 * S;
  x.setLineDash([10 * S, 8 * S]);
  x.beginPath();
  x.moveTo(cx0 + 34 * S, dy);
  x.lineTo(cx0 + cw - 34 * S, dy);
  x.stroke();
  x.restore();
  [cx0, cx0 + cw].forEach((nx) => {
    x.beginPath();
    x.arc(nx, dy, 17 * S, 0, Math.PI * 2);
    x.fillStyle = bg;
    x.fill();
    x.strokeStyle = "#e2e8f0";
    x.lineWidth = 2;
    x.stroke();
  });

  // stub
  text((codeLabel || "TICKET CODE"), W / 2, 770, 12, "600", "#94a3b8");
  x.fillStyle = "#0f172a";
  x.font = `bold ${32 * S}px ui-monospace, Menlo, monospace`;
  x.fillText(code, W / 2, 812 * S);

  const dataUrl = cv.toDataURL("image/png");
  cv.width = 0;
  cv.height = 0;
  return dataUrl;
}

export const isIOSNative = () => Capacitor.isNativePlatform() && Capacitor.getPlatform() === "ios";

export async function openExternal(url) {
  if (Capacitor.isNativePlatform()) {
    try {
      const { Browser } = await import("@capacitor/browser");
      await Browser.open({ url });
      return;
    } catch {}
  }
  window.open(url, "_blank", "noopener");
}

export function composeCertificatePng({
  heading, headingEn, bodyTop, name, bodyMid, highlight, highlightColor,
  detailsLine, footerNote, signName, signRole, qrCanvas, qrCaption, signatureImg, brand,
}) {
  const S = 2;
  const W = 1120 * S, H = 790 * S;
  const cv = document.createElement("canvas");
  cv.width = W;
  cv.height = H;
  const x = cv.getContext("2d");
  const font = (px, weight) => `${weight} ${px * S}px -apple-system, "Segoe UI", "Noto Sans Arabic", sans-serif`;

  x.fillStyle = "#ffffff";
  x.fillRect(0, 0, W, H);

  // gradient top bar
  const grad = x.createLinearGradient(0, 0, W, 0);
  grad.addColorStop(0, "#2563eb");
  grad.addColorStop(0.65, "#3b82f6");
  grad.addColorStop(1, "#f59e0b");
  x.fillStyle = grad;
  x.fillRect(0, 0, W, 12 * S);

  // formal borders
  x.strokeStyle = "#cbd5e1";
  x.lineWidth = 1.5 * S;
  x.strokeRect(24 * S, 30 * S, W - 48 * S, H - 54 * S);
  x.strokeStyle = "#e2e8f0";
  x.lineWidth = 1 * S;
  x.strokeRect(32 * S, 38 * S, W - 64 * S, H - 70 * S);

  x.textAlign = "center";
  const text = (str, xx, y, px, weight, color) => {
    if (!str) return;
    x.fillStyle = color;
    x.font = font(px, weight);
    x.fillText(str, xx, y * S);
  };

  // brand badge
  const bGrad = x.createLinearGradient(W / 2 - 28 * S, 62 * S, W / 2 + 28 * S, 118 * S);
  bGrad.addColorStop(0, "#2563eb");
  bGrad.addColorStop(1, "#f59e0b");
  x.fillStyle = bGrad;
  roundRectPath(x, W / 2 - 28 * S, 62 * S, 56 * S, 56 * S, 15 * S);
  x.fill();
  text("V", W / 2, 102, 32, "bold", "#ffffff");

  text(brand || "منصة تسجيل الفعاليات", W / 2, 146, 16, "bold", "#2563eb");

  const dGrad = x.createLinearGradient(W / 2 - 110 * S, 0, W / 2 + 110 * S, 0);
  dGrad.addColorStop(0, "#2563eb");
  dGrad.addColorStop(0.5, "#cbd5e1");
  dGrad.addColorStop(1, "#f59e0b");
  x.fillStyle = dGrad;
  x.fillRect(W / 2 - 110 * S, 160 * S, 220 * S, 3 * S);

  text(heading, W / 2, 218, 42, "bold", "#0f172a");
  text((headingEn || "").split("").join(" "), W / 2, 246, 10, "600", "#94a3b8");

  text(bodyTop, W / 2, 300, 16, "500", "#475569");
  text(name, W / 2, 348, 36, "bold", "#1d4ed8");
  text(bodyMid, W / 2, 386, 16, "500", "#475569");
  text(highlight, W / 2, 432, 30, "bold", highlightColor || "#0f172a");
  text(detailsLine, W / 2, 466, 14, "500", "#64748b");
  text(footerNote, W / 2, 496, 12, "500", "#94a3b8");

  // QR (start/right corner in RTL)
  if (qrCanvas) {
    const qs = 110 * S;
    const qx = W - 110 * S - qs;
    const qy = 560 * S;
    x.fillStyle = "#ffffff";
    x.fillRect(qx - 8 * S, qy - 8 * S, qs + 16 * S, qs + 16 * S);
    x.imageSmoothingEnabled = false;
    x.drawImage(qrCanvas, qx, qy, qs, qs);
    x.imageSmoothingEnabled = true;
    text(qrCaption, qx + qs / 2, 700, 10, "500", "#94a3b8");
  }

  // signature (end/left corner)
  const sigCX = 230 * S;
  if (signatureImg && signatureImg.complete && signatureImg.naturalWidth > 0) {
    const sh = 62 * S;
    const sw = (signatureImg.naturalWidth / signatureImg.naturalHeight) * sh;
    try {
      x.drawImage(signatureImg, sigCX - sw / 2, 570 * S, sw, sh);
    } catch {}
  }
  x.fillStyle = "#cbd5e1";
  x.fillRect(sigCX - 100 * S, 642 * S, 200 * S, 1 * S);
  text(signName || "Muhammed", sigCX / S === 0 ? W / 2 : sigCX, 668, 15, "bold", "#0f172a");
  text(signRole || "مدير منصة تسجيل الفعاليات", sigCX, 690, 12, "500", "#64748b");

  const dataUrl = cv.toDataURL("image/png");
  cv.width = 0;
  cv.height = 0;
  return dataUrl;
}

export async function collectPageCss() {
  let css = "";
  document.querySelectorAll("style").forEach((s) => {
    css += s.innerHTML + "\n";
  });
  const links = Array.from(document.querySelectorAll('link[rel="stylesheet"]'));
  const texts = await Promise.all(
    links.map((l) => fetch(l.href).then((r) => (r.ok ? r.text() : "")).catch(() => ""))
  );
  return css + texts.join("\n");
}

export async function printHtml(content, name = "document", orientation = "portrait") {
  const { Printer } = await import("@cabletv102/capacitor-plugin-printer");
  await new Promise((r) => setTimeout(r, 100));
  await Printer.print({ content, name, orientation });
}

export function imgToDataUrl(imgEl, maxH = 120) {
  if (!imgEl || !imgEl.complete || !imgEl.naturalWidth) return "";
  try {
    const h = Math.min(maxH, imgEl.naturalHeight);
    const w = (imgEl.naturalWidth / imgEl.naturalHeight) * h;
    const c = document.createElement("canvas");
    c.width = Math.round(w);
    c.height = Math.round(h);
    c.getContext("2d").drawImage(imgEl, 0, 0, c.width, c.height);
    const out = c.toDataURL("image/png");
    c.width = 0;
    c.height = 0;
    return out;
  } catch {
    return "";
  }
}

export async function printCertificateHtml({
  heading, headingEn, bodyTop, name, bodyMid, highlight, highlightColor,
  detailsLine, footerNote, qrDataUrl, qrCaption, signatureDataUrl, signName, signRole, brand, fileName,
}) {
  const html = `<!DOCTYPE html><html dir="rtl"><head><meta charset="utf-8"/><style>
@page{size:A4 landscape;margin:0}
*{-webkit-print-color-adjust:exact;print-color-adjust:exact;box-sizing:border-box}
body{margin:0;font-family:-apple-system,'Segoe UI','Noto Sans Arabic',sans-serif;color:#0f172a}
.sheet{position:relative;width:297mm;height:207mm;overflow:hidden;text-align:center;padding:16mm 24mm 0}
.bar{position:absolute;top:0;left:0;right:0;height:6mm;background:linear-gradient(90deg,#2563eb,#3b82f6,#f59e0b)}
.b1{position:absolute;top:11mm;bottom:9mm;left:9mm;right:9mm;border:1.5px solid #cbd5e1}
.b2{position:absolute;top:13.5mm;bottom:11.5mm;left:11.5mm;right:11.5mm;border:1px solid #e2e8f0}
.badge{display:block;width:15mm;height:15mm;line-height:15mm;border-radius:4mm;background:linear-gradient(135deg,#2563eb,#f59e0b);color:#fff;font-weight:800;font-size:24px;margin:2mm auto 0}
.brand{display:block;margin-top:2.5mm;font-size:13px;font-weight:800;color:#2563eb}
.div{display:block;width:55mm;height:1mm;margin:3mm auto;background:linear-gradient(90deg,#2563eb,#cbd5e1,#f59e0b);border-radius:1mm}
h1{display:block;font-size:33px;margin:1mm 0 0;font-weight:800;line-height:1.25}
.en{display:block;font-size:9px;letter-spacing:5px;color:#94a3b8;margin-top:1.5mm}
.body{display:block;font-size:14px;color:#475569;margin-top:6mm}
.name{display:block;font-size:29px;font-weight:800;color:#1d4ed8;margin-top:2.5mm;line-height:1.3}
.mid{display:block;font-size:14px;color:#475569;margin-top:2mm}
.hl{display:block;font-size:23px;font-weight:800;margin-top:2mm;line-height:1.3;color:${highlightColor || "#0f172a"}}
.det{display:block;font-size:12px;color:#64748b;margin-top:2.5mm}
.note{display:block;font-size:10.5px;color:#94a3b8;margin-top:2mm}
.bottom{position:absolute;bottom:15mm;left:26mm;right:26mm}
.btable{display:table;width:100%;table-layout:fixed}
.cell{display:table-cell;width:50%;vertical-align:bottom;text-align:center;height:32mm}
.qr-cap{display:block;font-size:8px;color:#94a3b8;margin-top:1mm;font-family:ui-monospace,Menlo,monospace}
.sig-img{display:block;height:13mm;margin:0 auto}
.sig-line{display:block;width:48mm;height:1px;background:#cbd5e1;margin:2mm auto 1.5mm}
.sig-name{display:block;font-size:12px;font-weight:800}
.sig-role{display:block;font-size:10px;color:#64748b;margin-top:0.5mm}
</style></head><body>
<div class="sheet">
  <div class="bar"></div><div class="b1"></div><div class="b2"></div>
  <span class="badge">V</span>
  <span class="brand">${brand || "منصة تسجيل الفعاليات"}</span>
  <span class="div"></span>
  <h1>${heading}</h1>
  <span class="en">${headingEn || ""}</span>
  <span class="body">${bodyTop}</span>
  <span class="name">${name}</span>
  <span class="mid">${bodyMid}</span>
  <span class="hl">${highlight}</span>
  ${detailsLine ? `<span class="det">${detailsLine}</span>` : ""}
  ${footerNote ? `<span class="note">${footerNote}</span>` : ""}
  <div class="bottom"><div class="btable">
    <div class="cell">
      ${qrDataUrl ? `<img src="${qrDataUrl}" width="85" height="85" style="display:inline-block;image-rendering:pixelated"/>` : ""}
      ${qrCaption ? `<span class="qr-cap">${qrCaption}</span>` : ""}
    </div>
    <div class="cell">
      ${signatureDataUrl ? `<img class="sig-img" src="${signatureDataUrl}"/>` : `<span style="display:block;height:13mm"></span>`}
      <span class="sig-line"></span>
      <span class="sig-name">${signName || "Muhammed"}</span>
      <span class="sig-role">${signRole || "مدير منصة تسجيل الفعاليات"}</span>
    </div>
  </div></div>
</div>
</body></html>`;
  await printHtml(html, fileName || "certificate", "landscape");
}

export async function printTicketHtml({ code, name, eventTitle, eventDate, qrDataUrl, brand, ticketLabel, nameLabel, dateLabel }) {
  const info = (label, value) =>
    value
      ? `<div class="cell"><div class="ilabel">${label}</div><div class="ivalue">${value}</div></div>`
      : "";
  const html = `<!DOCTYPE html><html dir="rtl"><head><meta charset="utf-8"/><style>
@page{size:A4 portrait;margin:0}
*{-webkit-print-color-adjust:exact;print-color-adjust:exact;box-sizing:border-box}
body{margin:0;font-family:-apple-system,'Segoe UI','Noto Sans Arabic',sans-serif;color:#0f172a;background:#f1f5f9;padding:18mm 0}
.ticket{width:108mm;margin:0 auto;position:relative}
.card{background:#fff;border:1.5px solid #e2e8f0;border-radius:22px;overflow:hidden}
.bar{height:3mm;background:linear-gradient(90deg,#2563eb,#3b82f6,#f59e0b)}
.main{padding:7mm 8mm 6mm}
.hrow{display:table;width:100%}
.hcell{display:table-cell;vertical-align:middle}
.badge{display:inline-block;width:10mm;height:10mm;line-height:10mm;text-align:center;border-radius:3mm;background:linear-gradient(135deg,#2563eb,#f59e0b);color:#fff;font-weight:800;font-size:17px;vertical-align:middle}
.bwrap{display:inline-block;vertical-align:middle;margin-inline-start:2.5mm;text-align:start}
.bname{font-size:10.5px;font-weight:800;color:#2563eb}
.bsub{font-size:7px;letter-spacing:3px;color:#94a3b8;margin-top:0.5mm}
.pill{float:inline-end;border:1px solid #a7f3d0;background:#ecfdf5;color:#047857;font-size:9px;font-weight:800;border-radius:99px;padding:1.5mm 3.5mm;margin-top:1mm}
.title{margin-top:5mm;font-size:19px;font-weight:800;text-align:start}
.grid{display:table;width:100%;margin-top:4mm}
.cell{display:table-cell;width:50%;text-align:start}
.ilabel{font-size:8px;letter-spacing:2px;text-transform:uppercase;color:#94a3b8}
.ivalue{font-size:12.5px;font-weight:700;margin-top:1mm}
.qrwrap{text-align:center;margin-top:6mm}
.qrbox{display:inline-block;padding:4mm;border:2px solid #e2e8f0;border-radius:5mm;background:#fff}
.perf{position:relative;height:8mm;border-left:1.5px solid #e2e8f0;border-right:1.5px solid #e2e8f0;background:#fff}
.dash{position:absolute;top:50%;left:7mm;right:7mm;border-top:2px dashed #cbd5e1}
.notch{position:absolute;top:50%;width:8mm;height:8mm;border-radius:50%;background:#f1f5f9;border:1.5px solid #e2e8f0;transform:translateY(-50%)}
.nl{left:-4mm}.nr{right:-4mm}
.stub{background:#fff;border:1.5px solid #e2e8f0;border-top:0;border-radius:0 0 22px 22px;text-align:center;padding:5mm 8mm 6mm}
.clabel{font-size:8px;letter-spacing:4px;color:#94a3b8}
.code{font-family:ui-monospace,Menlo,monospace;font-size:21px;font-weight:800;letter-spacing:2px;margin-top:1.5mm}
</style></head><body>
<div class="ticket">
  <div class="card" style="border-bottom:0;border-radius:22px 22px 0 0">
    <div class="bar"></div>
    <div class="main">
      <div class="hrow"><div class="hcell" style="text-align:start">
        <span class="badge">V</span>
        <span class="bwrap"><span class="bname" style="display:block">${brand || "منصة تسجيل الفعاليات"}</span><span class="bsub" style="display:block">E-TICKET</span></span>
      </div><div class="hcell" style="text-align:end"><span class="pill">${ticketLabel || "تذكرتك"}</span></div></div>
      ${eventTitle ? `<div class="title">${eventTitle}</div>` : ""}
      <div class="grid">${info(nameLabel || "الاسم", name)}${info(dateLabel || "التاريخ", eventDate)}</div>
      <div class="qrwrap"><div class="qrbox">${qrDataUrl ? `<img src="${qrDataUrl}" width="170" height="170" style="display:block;image-rendering:pixelated"/>` : ""}</div></div>
    </div>
  </div>
  <div class="perf"><div class="dash"></div><div class="notch nl"></div><div class="notch nr"></div></div>
  <div class="stub">
    <div class="clabel">TICKET CODE</div>
    <div class="code">${code}</div>
  </div>
</div>
</body></html>`;
  await printHtml(html, `ticket-${code}`, "portrait");
}

export async function printImageDataUrl(dataUrl, name = "document", orientation = "portrait") {
  const { Printer } = await import("@cabletv102/capacitor-plugin-printer");
  let jpeg = await new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const ratio = Math.min(1, 1920 / img.width, 1080 / img.height);
      const c = document.createElement("canvas");
      c.width = Math.round(img.width * ratio);
      c.height = Math.round(img.height * ratio);
      const ctx = c.getContext("2d");
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, c.width, c.height);
      ctx.drawImage(img, 0, 0, c.width, c.height);
      const out = c.toDataURL("image/jpeg", 0.6);
      setTimeout(() => {
        c.width = 0;
        c.height = 0;
      }, 50);
      img.src = "";
      resolve(out);
    };
    img.onerror = () => resolve(dataUrl);
    img.src = dataUrl;
  });
  try {
    await new Promise((r) => setTimeout(r, 100));
    await Printer.print({
      content: `<html><head><meta name="viewport" content="width=device-width"/></head><body style="margin:0"><img src="${jpeg}" style="max-width:100%; height:auto; display:block; margin:0 auto;"/></body></html>`,
      name,
      orientation,
    });
  } finally {
    jpeg = null;
  }
}

export async function saveBlobFile(blob, filename) {
  if (Capacitor.isNativePlatform()) {
    const base64 = await new Promise((resolve) => {
      const fr = new FileReader();
      fr.onload = () => resolve(String(fr.result).split(",")[1]);
      fr.readAsDataURL(blob);
    });
    const res = await Filesystem.writeFile({ path: filename, data: base64, directory: Directory.Cache });
    await Share.share({ files: [res.uri] });
    return;
  }
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}

export async function shareImageDataUrl({ title, text, url, dataUrl, fileName = "ticket.png" }) {
  try {
    if (Capacitor.isNativePlatform()) {
      const base64 = dataUrl.split(",")[1];
      const res = await Filesystem.writeFile({ path: fileName, data: base64, directory: Directory.Cache });
      const fullText = [text, url].filter(Boolean).join("\n");
      await Share.share({ title, text: fullText || undefined, files: [res.uri] });
      return "shared";
    }
    if (navigator.canShare && dataUrl) {
      const blob = await (await fetch(dataUrl)).blob();
      const file = new File([blob], fileName, { type: "image/png" });
      if (navigator.canShare({ files: [file] })) {
        await navigator.share({ title, text, url, files: [file] });
        return "shared";
      }
    }
    if (!url && dataUrl) {
      const a = document.createElement("a");
      a.href = dataUrl;
      a.download = fileName;
      a.click();
      return "downloaded";
    }
    return await shareLink({ title, text, url });
  } catch (e) {
    if (e && (e.name === "AbortError" || `${e.message}`.includes("canceled"))) return "shared";
    if (!url && dataUrl) return "shared";
    return await shareLink({ title, text, url });
  }
}


export async function hapticSuccess() {
  try {
    if (Capacitor.isNativePlatform()) {
      await Haptics.notification({ type: NotificationType.Success });
    } else if (navigator.vibrate) {
      navigator.vibrate(120);
    }
  } catch {}
}

export async function hapticError() {
  try {
    if (Capacitor.isNativePlatform()) {
      await Haptics.notification({ type: NotificationType.Error });
    } else if (navigator.vibrate) {
      navigator.vibrate([80, 60, 80]);
    }
  } catch {}
}

export async function shareLink({ title, text, url }) {
  try {
    if (Capacitor.isNativePlatform()) {
      await Share.share({ title, text, url });
      return "shared";
    }
    if (navigator.share) {
      await navigator.share({ title, text, url });
      return "shared";
    }
    await navigator.clipboard.writeText(url);
    return "copied";
  } catch {
    return null;
  }
}

const TICKETS_KEY = "saved_tickets_v1";
export function saveTicketLocal(ticket) {
  try {
    const list = getLocalTickets().filter((x) => x.code !== ticket.code);
    list.unshift({ ...ticket, saved_at: new Date().toISOString() });
    localStorage.setItem(TICKETS_KEY, JSON.stringify(list.slice(0, 50)));
  } catch {}
}

export function getLocalTickets() {
  try {
    return JSON.parse(localStorage.getItem(TICKETS_KEY) || "[]");
  } catch {
    return [];
  }
}

export async function relinkDevice(apiPost) {
  try {
    if (!Capacitor.isNativePlatform()) return;
    const token = localStorage.getItem("push_token");
    if (token) await apiPost("/devices", { token, platform: Capacitor.getPlatform() });
  } catch {}
}

export async function unlinkDevice(apiPost) {
  try {
    if (!Capacitor.isNativePlatform()) return;
    const token = localStorage.getItem("push_token");
    if (token) await apiPost("/devices/unlink", { token, platform: Capacitor.getPlatform() });
  } catch {}
}

let _pushNavigate = null;
let _pendingPushUrl = null;

export function setPushNavigator(navigateFn) {
  _pushNavigate = navigateFn;
  if (_pendingPushUrl) {
    const url = _pendingPushUrl;
    _pendingPushUrl = null;
    navigateFn(url);
  }
}

function handlePushUrl(url) {
  if (!url || typeof url !== "string" || !url.startsWith("/")) return;
  if (_pushNavigate) _pushNavigate(url);
  else _pendingPushUrl = url;
}

let _pushInited = false;
export async function initPushNotifications(apiPost) {
  if (!Capacitor.isNativePlatform() || _pushInited) return;
  _pushInited = true;
  try {
    const { PushNotifications } = await import("@capacitor/push-notifications");
    PushNotifications.addListener("pushNotificationActionPerformed", (action) => {
      const d = action?.notification?.data || {};
      let url = d.url;
      if (!url && d.type === "event_reminder" && (d.ticket_code || d.id)) url = `/ticket/${d.ticket_code || d.id}`;
      if (!url && d.type === "certificate_ready" && (d.ticket_code || d.id)) url = `/certificate/${d.ticket_code || d.id}`;
      handlePushUrl(url);
    });
    PushNotifications.addListener("pushNotificationReceived", async (n) => {
      try {
        const { toast } = await import("sonner");
        toast.info(n?.title || "", { description: n?.body || "" });
      } catch {}
    });
    let perm = await PushNotifications.checkPermissions();
    if (perm.receive === "prompt") perm = await PushNotifications.requestPermissions();
    if (perm.receive !== "granted") return;
    PushNotifications.addListener("registration", (token) => {
      try { localStorage.setItem("push_token", token.value); } catch {}
      apiPost("/devices", { token: token.value, platform: Capacitor.getPlatform() }).catch(() => {});
    });
    await PushNotifications.register();
  } catch {}
}
